/* eslint-disable no-console */
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { setOpenErrorsTabCertification } from '../../../actions/CertificationActions';
import { setOpenErrorsTabEntityDisplay } from '../../../actions/EntityDisplayActions';
import { getFormModel } from '../../../api/forms';
import { getSupportDataSync } from '../../../api/get-support-data';
import smartSolveApi from '../../../api/instances/smartsolve';
import { getTranslationSync } from '../../../api/translations';
import { CERTIFICATION_PAGE, ENTITY_DISPLAY } from '../../../constants/Urls';
import { WARNING_SOURCES } from '../../../constants/WarningsConstants';
import {
  logRulesChainingDepth,
  logUpdateRulesEvaluationResult,
  logValidationRulesEvaluationResult,
} from '../../../utils/log';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import { FORM_RELEASE_TYPE_PILGRIM } from '../../constants/editor.constants';
import { SOURCE_TYPES } from '../../constants/form-manager.constants';
import { ACTION_TYPE_SET_DEFAULT, ACTION_TYPE_SET_ERROR } from '../../constants/runtime.constants';
import { evalSSEexpression, evaluateConditions } from '../evaluation.utils';
import {
  getDataPath,
  getDataType,
  getFullDataSetByDataMember,
  getGridArrIndexBasedOnRequiredLength,
  getRelationShipKey,
  getSupportDataRequestFilter,
} from '../form-manager.utils';
import {
  getDataForController,
  getDataMembersIndexesMap,
  getGridArrIndexForRowFilter,
  getOrderedRules,
  getValueWithDataType,
  isControlInsideComplexGridColumn,
  setContainersInfoForWarning,
} from '../runtime.utils';
import { getControls } from '../validationStorage.utils';

const makeGetControlValue = (data, entityDefinition) => ({ entityName, fieldName, index }) =>
  getDataForController({
    entityDefinition,
    entityData: data.entityDataStore.entityData,
    entityName,
    fieldName,
    index,
  });

export const setFieldValue = (data, entityDefinition, fieldData) => {
  const { fieldName, entityName, index, value } = fieldData;

  const pathWithIndexes = getDataPath(entityName, index, entityDefinition);
  const path = `entityDataStore.entityData.${pathWithIndexes.join('.')}.${fieldName}`;

  _.set(data, path, value);
};

const getControlsByProperties = (formModel, properties) =>
  Object.values(formModel).filter((control) => {
    let condition = true;

    for (const prop in properties) {
      condition &= control[prop] === properties[prop];
    }

    return condition;
  });

export const getControlsByFormChanges = (changes = [], formModel) => {
  const controlsToApplyUpdate = [];

  changes.forEach((change) => {
    const matchControls = getControlsByProperties(formModel, {
      entityName: change.entityName,
      fieldName: change.fieldName,
    });

    matchControls.forEach((matchControl) => {
      controlsToApplyUpdate.push([matchControl, change.index]);
    });
  });

  return controlsToApplyUpdate;
};

export const processSourceValue = ({ action, expression, ssContext }) => {
  const { sourceMapping, sourceType, source, sourceFilter, target } = action;
  const {
    dataSources,
    data,
    translations: { languages },
    relationShipKeysMap,
    gridArrIndex,
    entityDefinition,
  } = ssContext;

  let filteredRows = [];
  let srcValue;

  if (expression || sourceType === 'formula') {
    srcValue = evalSSEexpression(expression ?? sourceMapping, ssContext) ?? '';
  }

  switch (sourceType) {
    case SOURCE_TYPES.DATASOURCE: {
      if (!dataSources || !source) {
        break;
      }

      const actionConditions = sourceFilter?.Conditions || [];
      const dataSource = dataSources[source];

      if (!dataSource) {
        console.log('📦 No data source found for', source);
        break;
      }

      const dataSourceConditions = dataSource?.filter?.Conditions || [];

      if (dataSource.dataSourceTypeStr === 'FORMDATA') {
        filteredRows = getFullDataSetByDataMember(data, dataSource.dataSourceName).filter((row) =>
          evaluateConditions({
            conditions: actionConditions,
            ssContext,
            datasourceRow: row,
          })
        );
      } else if (dataSource.dataSourceTypeStr === 'SETTINGS') {
        const allFilters = getSupportDataRequestFilter(
          [...actionConditions, ...dataSourceConditions],
          ssContext
        );
        filteredRows = Object.entries(languages)
          .map(([key, value]) => ({
            LANGUAGE_CODE: value,
            LANGUAGE_NAME: key,
          }))
          .filter((row) =>
            evaluateConditions({
              conditions: allFilters,
              ssContext,
              datasourceRow: row,
            })
          );
      } else {
        const additionalFilters = getSupportDataRequestFilter(
          [...actionConditions, ...dataSourceConditions],
          ssContext
        );
        const dataSourceName = dataSource.dataSourceName ?? '';
        const cachedSupportData = ssContext.cachedSupportData;
        const supportDataItem = _.get(cachedSupportData, dataSourceName);
        let response;

        if (!isNilOrEmpty(supportDataItem)) {
          response = supportDataItem;
        } else {
          response = getSupportDataSync({ ...dataSource, filter: additionalFilters });
        }

        filteredRows = (
          response?.GetSupportData?.rows.map((row) =>
            _.zipObject(dataSource.runtimeColumnList, row?.cells)
          ) ?? []
        ).filter((datasourceRow) =>
          evaluateConditions({ conditions: actionConditions, ssContext, datasourceRow })
        );
      }

      if (filteredRows.length === 1 && sourceMapping?.length > 0) {
        srcValue = filteredRows[0][sourceMapping];
      } else {
        console.log('📦 Results from data fetch do not meet requirements for update, skipping');
      }
      break;
    }

    case SOURCE_TYPES.TEXT:
      srcValue = sourceMapping;
      break;

    case SOURCE_TYPES.DATE:
      srcValue = sourceMapping;
      break;

    case SOURCE_TYPES.NUMBER:
      srcValue = +sourceMapping;
      break;

    case SOURCE_TYPES.BOOLEAN:
      srcValue = sourceMapping === 'true';
      break;

    case SOURCE_TYPES.COLUMN: {
      const [entityName, fieldName] = sourceMapping.split('.');
      const sourceRelationshipKey = relationShipKeysMap[entityName];
      const is1to1 = sourceRelationshipKey === '1to1';

      let fixedGridArrIndex = is1to1
        ? getGridArrIndexBasedOnRequiredLength(entityName, gridArrIndex)
        : gridArrIndex;

      fixedGridArrIndex =
        entityName !== target && is1to1
          ? [...fixedGridArrIndex.slice(0, -1), 0]
          : fixedGridArrIndex;

      const srcPath = getDataPath(entityName, fixedGridArrIndex, entityDefinition);
      const fieldObject = _.get(data?.entityDataStore?.entityData, srcPath, {});

      srcValue = fieldObject[fieldName];
      break;
    }

    default:
      if (sourceType !== 'formula') {
        console.log(`action.sourceType has no case: ${sourceType}`);
      }
  }

  return { srcValue, filteredRows };
};

export const getEntityResolverSupportDataRequestParams = (
  entityResolverRule,
  defaultValue,
  op = 'EQ'
) => ({
  dataSourceName: entityResolverRule.resolver_type,
  dataSourceType: 3,
  dataKeyField: entityResolverRule.resolver_id,
  filter: [
    {
      openGroup: false,
      leftType: 'datasource',
      leftValue: entityResolverRule.resolver_field,
      op,
      rightType: 'column',
      rightValue: defaultValue,
      closeGroup: false,
      relationalOp: 'AND',
    },
  ],
  columnList: entityResolverRule.dataMappingList.map((dm) => dm.updateFrom),
  caseInsensitiveSearch: false,
  queryName: null,
  searchText: null,
  searchOption: null,
  pageNumber: 1,
  pageSize: 0,
  getTotalRows: false,
});

/**
 * Resolves fields based on entityResolverRules
 */
export const resolveFields = (ssContext, fieldData) => {
  const { entityName, fieldName, value, index } = fieldData;
  const {
    data,
    entityDefinition,
    translations: { entityResolverRules },
  } = ssContext;

  const entityResolverRule = entityResolverRules.find(
    (rule) => rule.tableName === entityName && rule.columnName === fieldName
  );

  if (!entityResolverRule) {
    return;
  }

  // clear fields if value is empty
  if (isNilOrEmpty(value)) {
    entityResolverRule.dataMappingList.forEach((dataMapping) => {
      setFieldValue(data, entityDefinition, {
        fieldName: dataMapping.updateTo,
        entityName,
        index,
        value: null,
      });
    });

    return;
  }
  const entityResolverSupportDataSourceName = entityResolverRule.resolver_type;
  const cachedSupportData = ssContext.cachedSupportData;
  const supportData = cachedSupportData[entityResolverSupportDataSourceName];

  const cachedSupportDataMap = _.get(supportData, entityResolverSupportDataSourceName, undefined);
  let supportDataResp;
  // do mapping/update based on entityResolverRule
  if (cachedSupportDataMap) {
    supportDataResp = cachedSupportDataMap;
  } else {
    supportDataResp = getSupportDataSync(
      getEntityResolverSupportDataRequestParams(entityResolverRule, value)
    );
  }

  if (supportDataResp) {
    // if supportDataResp has at least 1 row -> use first row
    const cells = _.get(supportDataResp, 'GetSupportData.rows[0].cells', []);

    if (!cells.length) {
      return;
    }

    entityResolverRule.dataMappingList.forEach((dataMapping, i) => {
      setFieldValue(data, entityDefinition, {
        fieldName: dataMapping.updateTo,
        entityName,
        index,
        value: cells[i],
      });
    });
  }
};

export const addAffectedField = (affectedFields, newField) => {
  if (affectedFields.some((field) => _.isEqual(field, newField))) {
    return;
  }

  affectedFields.push(newField);
};

const applyUpdate = (ssContext, affectedFields, update) => {
  const { data, entityDefinition } = ssContext;
  const { fieldName, entityName, index } = update;

  setFieldValue(data, entityDefinition, update);
  addAffectedField(affectedFields, { fieldName, entityName, index });
  logUpdateRulesEvaluationResult(update);
  resolveFields(ssContext, update);
};

const getFilteredIndexes = ({ conditions, indexes, ssContext, rows }) =>
  indexes.filter((targetGridArrIndex, i) =>
    evaluateConditions({
      conditions,
      ssContext: {
        ...ssContext,
        gridArrIndex: targetGridArrIndex,
      },
      datasourceRow: rows[i],
    })
  );

const getIndexesForTargetDataMember = (
  targetEntityName,
  targetRelationShipKey,
  targetFilter,
  ssContext
) => {
  if (targetEntityName === 'V_ENU_NONPERSIS_USER_DATA') {
    return [[0]];
  }

  const { contextDataMember, gridArrIndex, data, entityDefinition } = ssContext;
  const sourceEntityNameSplit = contextDataMember.split('-').length;
  const targetEntityNameSplit = targetEntityName.split('-').length;
  const conditions = targetFilter?.Conditions ?? [];

  if (Math.abs(targetEntityNameSplit - sourceEntityNameSplit) > 1) {
    // updating grandchildren or grandparents is not allowed
    return [];
  }

  if (targetEntityNameSplit > sourceEntityNameSplit) {
    const dataPath = getDataPath(targetEntityName, gridArrIndex, entityDefinition).slice(0, -1);
    const rows = _.get(data.entityDataStore.entityData, dataPath);

    if (!rows) {
      if (targetRelationShipKey === '1to1') {
        return sourceEntityNameSplit === 1 ? [[0]] : [[...gridArrIndex, 0]];
      }

      return [];
    }

    const indexes = rows.map((_, i) => (sourceEntityNameSplit === 1 ? [i] : [...gridArrIndex, i]));

    return conditions.length
      ? getFilteredIndexes({ conditions, indexes, ssContext, rows })
      : indexes;
  } else if (targetEntityNameSplit === sourceEntityNameSplit) {
    if (targetEntityName !== contextDataMember && targetEntityNameSplit !== 1) {
      const dataPath = getDataPath(targetEntityName, gridArrIndex, entityDefinition).slice(0, -1);
      const rows = _.get(data.entityDataStore.entityData, dataPath, []);

      const indexes = rows.map((_, i) =>
        targetEntityNameSplit === 2 ? [i] : [...gridArrIndex.slice(0, -1), i]
      );

      return conditions.length
        ? getFilteredIndexes({ conditions, indexes, ssContext, rows })
        : indexes;
    }

    return [gridArrIndex];
  } else {
    return targetEntityNameSplit === 1 ? [[0]] : [gridArrIndex.slice(0, -1)];
  }
};

export const shouldApplyActionUpdate = (currentValue, newValue, sourceType, isSetDefault) => {
  if (isSetDefault) {
    return !isNilOrEmpty(newValue);
  }

  return (
    currentValue !== newValue &&
    (sourceType !== 'datasource' || (sourceType === 'datasource' && !isNilOrEmpty(newValue)))
  );
};

export const shouldApplyActionMapping = (currentValue, newValue, isSetDefault) => {
  if (isSetDefault) {
    return isNilOrEmpty(currentValue) && !isNilOrEmpty(newValue);
  }

  return currentValue !== newValue;
};

const executeUpdateActions = ({
  rule,
  ssContext,
  ruleConditionMet = null,
  affectedFields,
  getControlValue,
}) => {
  const { actions, id: ruleId } = rule;
  const { entityDefinition, entityType, relationShipKeysMap } = ssContext;

  actions.forEach((action) => {
    const {
      sourceMapping,
      sourceType,
      targetMapping,
      type,
      mapping,
      conditionMet,
      target,
      targetFilter,
      id: actionId,
    } = action;

    if (ruleConditionMet !== null && ruleConditionMet !== conditionMet) {
      return;
    }

    const isSetDefault = type === ACTION_TYPE_SET_DEFAULT;
    const expression = sourceMapping.split('eval||')[1];

    const targetMappingSplit = targetMapping.split('.');
    const targetEntityName = targetMappingSplit[0] || target;
    const targetFieldName = targetMappingSplit[1];

    const logInfo = {
      ruleId,
      actionId,
      actionType: type,
    };

    const targetDataMemberIndexesArray = getIndexesForTargetDataMember(
      targetEntityName,
      relationShipKeysMap[targetEntityName],
      targetFilter,
      ssContext
    );

    targetDataMemberIndexesArray.forEach((gridArrIndex) => {
      const currentValue = getControlValue({
        entityName: targetEntityName,
        fieldName: targetFieldName,
        index: gridArrIndex,
      });

      if (isSetDefault && !isNilOrEmpty(currentValue)) {
        return;
      }

      const { srcValue, filteredRows } = processSourceValue({
        action,
        expression,
        ssContext,
      });

      // action update
      if (shouldApplyActionUpdate(currentValue, srcValue, sourceType, isSetDefault)) {
        const castedSrcValue = getValueWithDataType(
          srcValue,
          entityDefinition,
          entityType,
          targetEntityName,
          targetFieldName
        );

        applyUpdate(ssContext, affectedFields, {
          value: castedSrcValue,
          entityName: targetEntityName,
          fieldName: targetFieldName,
          index: gridArrIndex,
          logInfo,
        });
      }

      if (!Array.isArray(mapping) || !filteredRows.length) {
        return;
      }

      // action mapping
      mapping.forEach((mapItem) => {
        const [entityName, fieldName] = mapItem.To.split('.');
        const currentValue = getControlValue({
          entityName,
          fieldName,
          index: gridArrIndex,
        });
        const srcMappingValue = filteredRows[0][mapItem.From];

        if (shouldApplyActionMapping(currentValue, srcMappingValue, isSetDefault)) {
          const castedSrcMappingValue = getValueWithDataType(
            srcMappingValue,
            entityDefinition,
            entityType,
            entityName,
            fieldName
          );

          applyUpdate(ssContext, affectedFields, {
            value: castedSrcMappingValue,
            entityName,
            fieldName,
            index: gridArrIndex,
            logInfo: { ...logInfo, isMapping: true },
          });
        }
      });
    });
  });
};

/**
 * Flow:
 * 1. Receives controls, runs their `update` rules and creates affectedFields array, which is the fields affected by rules.
 * 2. Based on the affectedFields, finds corresponding controls in formModel.
 * 3. Runs `update` rules for these controls.
 * 4. Repeat until affectedFields array is empty.
 */
export const runUpdateRules = ({
  controls,
  ssContext,
  formRules,
  isDataChangedRef,
  chainingDepth = 0,
}) => {
  logRulesChainingDepth(chainingDepth);

  const { data, entityDefinition, entityType, formModel } = ssContext;
  const getControlValue = makeGetControlValue(data, entityDefinition);

  const affectedFields = [];
  const orderedFormRules = getOrderedRules(formRules);

  // form rules
  if (orderedFormRules.length) {
    const dataMembers = [...new Set(orderedFormRules.map((rule) => rule.entityName))];
    const dataMembersIndexesMap = getDataMembersIndexesMap(
      dataMembers,
      data.entityDataStore.entityData ?? {},
      entityType
    );

    orderedFormRules.forEach((rule) => {
      const dataMemberIndexes = dataMembersIndexesMap[rule.entityName];
      const isOneToOne = getRelationShipKey(entityDefinition, rule.entityName) === '1to1';

      if (isOneToOne && !dataMemberIndexes.length) {
        // 1to1 entities can be created by rules
        dataMemberIndexes.push([0]);
      }

      dataMemberIndexes.forEach((gridArrIndex) => {
        const _ssContext = { ...ssContext, gridArrIndex, contextDataMember: rule.entityName };
        const args = { rule, ssContext: _ssContext, affectedFields, getControlValue };

        if (rule.Conditions.length) {
          const result = evaluateConditions({
            conditions: rule.Conditions,
            ssContext: _ssContext,
          });

          executeUpdateActions({
            ...args,
            ruleConditionMet: result,
          });
        } else {
          // Execute actions for rules that don't have conditions specified.
          executeUpdateActions(args);
        }
      });
    });

    if (affectedFields.length && isDataChangedRef) {
      isDataChangedRef.flag = true;
    }
  }

  // control rules
  controls.forEach(([control, gridArrIndex]) => {
    if (!control.update?.rules?.length) {
      return;
    }

    for (const rule of control.update.rules) {
      const _ssContext = { ...ssContext, gridArrIndex, contextDataMember: rule.entityName };
      const args = { rule, ssContext: _ssContext, affectedFields, getControlValue };

      if (rule.Conditions.length) {
        const result = evaluateConditions({
          conditions: rule.Conditions,
          ssContext: _ssContext,
        });

        executeUpdateActions({
          ...args,
          ruleConditionMet: result,
        });
      } else {
        // Execute actions for rules that don't have conditions specified.
        executeUpdateActions(args);
      }
    }
  });

  // chaining
  if (affectedFields.length) {
    const controlsToApplyUpdate = getControlsByFormChanges(affectedFields, formModel); // controls that consume the changes

    runUpdateRules({
      controls: controlsToApplyUpdate,
      ssContext,
      formRules: [],
      chainingDepth: chainingDepth + 1,
    });
  }
};

const errorsTranslationsCache = {};

export const executeValidationActions = ({ ssContext, rule }) =>
  rule.actions.reduce((acc, action) => {
    const { parameter, type, parameterType } = action;

    if (type !== ACTION_TYPE_SET_ERROR) {
      return acc;
    }

    const controls = getControls();

    const { formModel, gridArrIndex, relationShipKeysMap } = ssContext;
    const [targetEntityName, targetFieldName] = parameter.split('.');
    const is1to1 = relationShipKeysMap[targetEntityName] === '1to1';
    const fixedGridArrIndex = is1to1
      ? getGridArrIndexBasedOnRequiredLength(targetEntityName, gridArrIndex)
      : gridArrIndex;

    let targetInstanceIds = [];

    switch (parameterType) {
      case 'column':
        targetInstanceIds = Object.values(formModel)
          .reduce((ids, control) => {
            if (
              control.entityName === targetEntityName &&
              control.fieldName === targetFieldName &&
              !isControlInsideComplexGridColumn(control.controlId, formModel)
            ) {
              return [...ids, `${control.controlId}#[${fixedGridArrIndex}]`];
            }
            return ids;
          }, [])
          .filter((instanceId) => instanceId in controls);
        break;

      case 'table':
      case 'control':
        targetInstanceIds = [''];
        break;

      default:
        break;
    }

    if (!targetInstanceIds.length) {
      return acc;
    }

    let message = '';

    if (errorsTranslationsCache[rule.errorMsg]) {
      message = errorsTranslationsCache[rule.errorMsg];
    } else {
      const apiData = getTranslationSync({ translationKey: rule.errorMsg });

      if (apiData && !apiData.hasError) {
        message = apiData.Translation;
      } else {
        message = `Error ${rule.errorMsg} with ${action.dummyParameter}`; // fallback error message
      }

      errorsTranslationsCache[rule.errorMsg] = message;
    }

    const warning = {
      instanceId: targetInstanceIds[0], // show warning for the 1-st control binded to entityName.fieldName in current row (old engine behavior)
      code: rule.errorCode,
      message,
      source: WARNING_SOURCES.RULES,
      warningEnabled: true,
    };

    setContainersInfoForWarning(warning);

    const logInfo = {
      actionType: type,
      entityName: targetEntityName,
      fieldName: targetFieldName,
      index: fixedGridArrIndex,
      message,
      actionId: action.id,
      ruleId: rule.id,
    };

    logValidationRulesEvaluationResult(logInfo);

    return [...acc, warning];
  }, []);

export const runValidationRules = ({ control, ssContext, validationRulesWarnings }) => {
  const { rules } = control.validation;

  if (!rules.length) {
    return;
  }

  const dataMembers = [...new Set(rules.map((rule) => rule.entityName))];
  const dataMembersIndexesMap = getDataMembersIndexesMap(
    dataMembers,
    ssContext.data.entityDataStore.entityData ?? {},
    ssContext.entityType
  );

  for (const rule of rules) {
    if (!rule.Conditions.length) {
      continue;
    }

    const warningsForRule = [];
    const dataMemberIndexes = dataMembersIndexesMap[rule.entityName];

    dataMemberIndexes.forEach((gridArrIndex) => {
      const _ssContext = {
        ...ssContext,
        gridArrIndex,
        contextDataMember: rule.entityName,
      };

      const conditionMet = evaluateConditions({
        conditions: rule.Conditions,
        ssContext: _ssContext,
      });

      if (!conditionMet) {
        return;
      }
      const warningsForCurrentIndex = executeValidationActions({
        ssContext: _ssContext,
        rule,
      });

      warningsForRule.push(...warningsForCurrentIndex);
    });

    validationRulesWarnings.push(...warningsForRule);
  }
};

export const clearTableByUniqueIdentifier = ({ control, entityName, ssContext }) => {
  const controlFilters = control.filter?.Conditions;
  const path = getDataPath(entityName, ssContext.gridArrIndex, ssContext.entityDefinition);
  const allRows = _.get(ssContext.data.entityDataStore?.entityData, path.slice(0, -1), []);

  const filteredArray = allRows.filter((row, rowIndex) => {
    return !evaluateConditions({
      conditions: controlFilters,
      ssContext: {
        ...ssContext,
        gridArrIndex: getGridArrIndexForRowFilter(ssContext.gridArrIndex, rowIndex),
      },
      datasourceRow: row,
    });
  });
  return filteredArray;
};

export const changeModelId = (formModel, id, newId) => {
  return Object.entries(formModel).reduce((acc, [key, model]) => {
    if (model.childrenNodes?.includes(id)) {
      // update parent
      acc[key] = {
        ...model,
        childrenNodes: model.childrenNodes.map((child) => (child === id ? newId : child)),
      };
    } else if (key === id) {
      // update model
      acc[newId] = {
        ...model,
        controlId: newId,
      };
    } else if (model.parent === id) {
      // update children
      acc[key] = {
        ...model,
        parent: newId,
      };
    } else {
      acc[key] = model;
    }

    return acc;
  }, {});
};

export const checkForLeftPanelWithErrors = (pathname) =>
  [ENTITY_DISPLAY, CERTIFICATION_PAGE].includes(pathname);

export const isCertificationPage = (pathname) => pathname === CERTIFICATION_PAGE;

export const getSetOpenErrorsTabAction = (pathname) => {
  switch (pathname) {
    case ENTITY_DISPLAY:
      return setOpenErrorsTabEntityDisplay;

    case CERTIFICATION_PAGE:
      return setOpenErrorsTabCertification;

    default:
      return;
  }
};

export const checkForBooleanDataType = ({ value, entityDefinition, entityName, fieldName }) => {
  if (isNilOrEmpty(value)) {
    return value;
  }

  const entityType = Object.keys(entityDefinition)[0];
  const dataType = getDataType(entityDefinition, entityType, entityName, fieldName);
  let ctrValue = value;

  if (dataType === 'System.Byte' || dataType === 'System.Boolean') {
    ctrValue = _.isBoolean(ctrValue) ? ctrValue : ctrValue === 'true' || ctrValue === '1';
  }

  return ctrValue;
};

export const cloneAndOmitDeep = (value, omitKeys) => {
  if (typeof value !== 'object') {
    return value;
  }

  const obj = _.clone(value);

  if (Array.isArray(obj)) {
    return obj.map((item) => cloneAndOmitDeep(item, omitKeys));
  }

  if (obj instanceof Date) {
    return obj;
  }

  const ommitedObj = _.omit(obj, omitKeys);

  Object.keys(ommitedObj).forEach((k) => {
    if (ommitedObj[k] !== null && typeof ommitedObj[k] === 'object') {
      ommitedObj[k] = cloneAndOmitDeep(ommitedObj[k], omitKeys);
    }
  });

  return ommitedObj;
};

export const getAutoCompleteOptionLabel = (textBoxChange, restChanges) => {
  const code = textBoxChange.value;

  return !restChanges.length
    ? code
    : `${code} (${restChanges.map((change) => change.value).join(' | ')})`;
};

export const handleNewSelectedSubGrid = (newSelectedSubGrid, selectedSubGrids) => {
  if (!selectedSubGrids.length) {
    return [newSelectedSubGrid];
  }

  if (newSelectedSubGrid.ssModalContext?.isInModal) {
    return [...selectedSubGrids, newSelectedSubGrid];
  }

  const selectedSubGridEntityNameSplit = newSelectedSubGrid.entityName.split('-').length;

  for (let i = 0; i < selectedSubGrids.length; i++) {
    const subGrid = selectedSubGrids[i];
    const subGridEntityNameSplit = subGrid.entityName.split('-').length;

    if (selectedSubGridEntityNameSplit > subGridEntityNameSplit) {
      if (newSelectedSubGrid.entityName.includes(subGrid.entityName)) {
        if (i === selectedSubGrids.length - 1) {
          return [...selectedSubGrids, newSelectedSubGrid];
        } else {
          continue;
        }
      } else {
        return [...selectedSubGrids.slice(0, i), newSelectedSubGrid];
      }
    } else {
      return [...selectedSubGrids.slice(0, i), newSelectedSubGrid];
    }
  }
};

export const processCopiedRow = (row) => {
  for (const key in row) {
    if (!Array.isArray(row[key])) {
      continue;
    }

    for (const childRow of row[key]) {
      childRow.IsNewObject = true;
      childRow._newRowId = uuidv4();

      processCopiedRow(childRow);
    }
  }

  return row;
};

export const getAllEntitiesByEntityName = (entityName) =>
  entityName
    .split('-')
    .reduce((acc, curr, i) => [...acc, i === 0 ? curr : `${acc[i - 1]}-${curr}`], []);

export const getZeroBaseGridArrIndex = (entityName) =>
  Array(entityName.split('-').length - 1).fill(0);

export const getReducedFields = (fields, formModel) => {
  const fieldsIdsMap = fields.reduce((acc, curr) => {
    acc[curr.controlId] = true;
    return acc;
  }, {});

  return fields.filter((f) => {
    let parentId = formModel[f.controlId].parent;

    while (parentId !== 'root') {
      if (parentId in fieldsIdsMap) {
        return false;
      }

      parentId = formModel[parentId].parent;
      continue;
    }

    return true;
  });
};

export const getPilgrimFormModel = async ({ formKey, formEntityKey }) => {
  const formHistoryResponse = await smartSolveApi.post('/forms/history', {
    formKey,
  });
  const { history = [] } = formHistoryResponse.data;

  if (!history.length) {
    return;
  }

  const pilgrimHistoryItems = history.filter(
    (h) => h.OPS_RELEASE_TYPE === FORM_RELEASE_TYPE_PILGRIM
  );

  pilgrimHistoryItems.sort((a, b) => b.OPS_VERSION - a.OPS_VERSION);

  const pilgrimFormModelId = pilgrimHistoryItems[0]?.OPS_ID; // use an item with the highest OPS_VERSION

  if (!pilgrimFormModelId) {
    return;
  }

  const pilgrimFormModelResponse = await getFormModel({
    formKey,
    formId: pilgrimFormModelId,
    formEntityKey,
    forEdit: true,
  });

  return pilgrimFormModelResponse.formDefinition.formModel;
};
